// src/Views/Pricing/Pricing.js
// UPDATED: February 1, 2026
// - 🔥 REMOVED: Free trial option for web (existing trials still honored)
// - Direct subscription flow only
// - V10 limits: 4 searches/day, 3 matches/search
// - iOS: $15/week, $50/month, $400/year
// - Web: $15/week, $50/month, $500/year
// - 🏀 Basketball V4 (Premium Exclusive)
// - 🎯 HRHR V5.5 (Premium Exclusive)

import React, { useMemo, useState } from 'react';
import './Pricing.css';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Capacitor } from '@capacitor/core'; 

// 🔥 WHOP URLS (Web only) - No trial URL needed anymore
const WHOP_WEEKLY_URL = "https://whop.com/predictai-labs/weekly-premium-ce";
const WHOP_MONTHLY_URL = "https://whop.com/predictai-labs/monthly-premium-plan"; 
const WHOP_YEARLY_URL = "https://whop.com/predictai-labs/yearly-premium-plan"; 

// 🔥 LEGAL URLS (Required by Apple)
const PRIVACY_POLICY_URL = "https://aipredict.app/privacy-policy";
const TERMS_OF_USE_URL = "https://aipredict.app/legal?tab=terms";

// 🔥 PRICING BY PLATFORM (in USD for display)
const PRICING = {
    ios: {
        weekly: 15,
        monthly: 50,
        yearly: 400
    },
    web: {
        weekly: 15,
        monthly: 50,
        yearly: 500
    }
};

const Pricing = () => {
    const navigate = useNavigate();
    
    const { 
        user, 
        isPremium,
        isPaidPremium,
        isTrialActive,      // Keep for existing trial users
        trialDaysLeft,      // Keep for existing trial users
        hasUsedTrial,       // Keep to detect expired trials
        offerings,
        purchasePackage,
        restorePurchases,
        loading
    } = useAuth();
    
    const [billingCycle, setBillingCycle] = useState('weekly');
    const [purchasing, setPurchasing] = useState(false);
    const [restoring, setRestoring] = useState(false);

    const isIOS = Capacitor.getPlatform() === 'ios';
    const isGuest = !user;

    // 🔥 Get correct pricing based on platform
    const prices = isIOS ? PRICING.ios : PRICING.web;
    const currentPrice = prices[billingCycle];

    // Period label
    const periodLabel = billingCycle === 'yearly' ? 'year' : billingCycle === 'monthly' ? 'month' : 'week';
    const periodLabelShort = billingCycle === 'yearly' ? 'yr' : billingCycle === 'monthly' ? 'mo' : 'wk';

    // Status text - 🔥 UPDATED: No trial mention for new users
    const statusText = useMemo(() => {
        if (loading) return 'Syncing...';
        if (isGuest) return 'Create an account to start.';
        if (isPaidPremium) return 'Active Premium Subscription';
        // Keep trial status for EXISTING trial users
        if (isTrialActive) return `${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} remaining on trial.`;
        if (hasUsedTrial) return 'Trial expired. Subscribe to continue.';
        // 🔥 NEW: Direct subscription messaging
        return 'Subscribe to unlock all features.';
    }, [loading, isGuest, isTrialActive, trialDaysLeft, isPaidPremium, hasUsedTrial]);

    // Button label - 🔥 UPDATED: No trial option
    const premiumButtonLabel = useMemo(() => {
        if (isGuest) return 'Sign In to Subscribe';
        if (isPaidPremium) return 'Plan Active';
        if (isTrialActive) return 'Upgrade Now';
        
        // 🔥 DIRECT SUBSCRIPTION for all users
        return `Subscribe ${billingCycle === 'yearly' ? 'Annually' : billingCycle === 'monthly' ? 'Monthly' : 'Weekly'} ($${currentPrice}/${periodLabelShort})`;
    }, [isGuest, isPaidPremium, billingCycle, isTrialActive, currentPrice, periodLabelShort]);

    // Get Whop URL based on billing cycle
    const getWhopUrl = () => {
        switch (billingCycle) {
            case 'yearly': return WHOP_YEARLY_URL;
            case 'monthly': return WHOP_MONTHLY_URL;
            case 'weekly': return WHOP_WEEKLY_URL;
            default: return WHOP_MONTHLY_URL;
        }
    };

    // Payment handler - 🔥 SIMPLIFIED: Direct to payment (no trial logic)
    const handleSubscribe = async () => {
        if (isGuest) {
            navigate('/signin');
            return;
        }

        if (isPaidPremium) {
            return; 
        }

        // iOS Native Purchase
        if (isIOS) {
            try {
                if (!offerings) {
                    alert("Connecting to App Store...");
                    return;
                }
                setPurchasing(true);
                
                let packageToBuy;
                if (billingCycle === 'yearly') {
                    packageToBuy = offerings.annual;
                } else if (billingCycle === 'monthly') {
                    packageToBuy = offerings.monthly;
                } else {
                    packageToBuy = offerings.weekly;
                }
                
                if (packageToBuy) {
                    await purchasePackage(packageToBuy);
                } else {
                    alert(`${billingCycle} plan not available. Please try another option.`);
                }
            } catch (e) {
                if (!e.userCancelled) {
                    console.error("Purchase Error:", e);
                    alert("Purchase failed. Please try again.");
                }
            } finally {
                setPurchasing(false);
            }
            return;
        }

        // Web Purchase (Whop) - 🔥 Direct to payment
        let checkoutUrl = getWhopUrl();
        if (user?.email) {
            const separator = checkoutUrl.includes('?') ? '&' : '?';
            checkoutUrl = `${checkoutUrl}${separator}email=${encodeURIComponent(user.email)}`;
        }
        window.location.href = checkoutUrl;
    };

    // Restore Purchases (iOS)
    const handleRestorePurchases = async () => {
        if (!restorePurchases) {
            alert("Restore not available on this platform.");
            return;
        }

        try {
            setRestoring(true);
            const restored = await restorePurchases();
            
            if (restored) {
                alert("✅ Purchases restored successfully! Your premium access has been activated.");
            } else {
                alert("No previous purchases found for this Apple ID.");
            }
        } catch (e) {
            console.error("Restore Error:", e);
            alert("Could not restore purchases. Please try again or contact support.");
        } finally {
            setRestoring(false);
        }
    };

    // Calculate savings
    const monthlyCostPerYear = prices.monthly * 12;
    const yearlySavingsVsMonthly = Math.round((1 - prices.yearly / monthlyCostPerYear) * 100);
    const monthlySavingsVsWeekly = Math.round((1 - prices.monthly / (prices.weekly * 4.33)) * 100);

    return (
        <section className="pricing">
            <div className="pricing__inner">

                <header className="pricing__header">
                    <div className="pricing__pill">
                        <Icon icon="solar:card-recive-linear" className="pricing__pill-icon" />
                        <span>Upgrade System</span>
                    </div>

                    <h1 className="pricing__title">
                        One subscription.<br />
                        <span className="pricing__highlight">Unlimited power.</span>
                    </h1>

                    <p className="pricing__subtitle">
                        Unlock the full potential of AiPredict. AiAssistant V10 Engine, Basketball AI, 
                        and HRHR value picks — all in one subscription.
                    </p>

                    {/* 🔥 BILLING TOGGLE - 3 options */}
                    <div className="pricing__toggle-container" style={{ 
                        display: 'flex', 
                        gap: '8px', 
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <button 
                            className={`pricing__cycle-btn ${billingCycle === 'weekly' ? 'pricing__cycle-btn--active' : ''}`}
                            onClick={() => setBillingCycle('weekly')}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '8px',
                                border: billingCycle === 'weekly' ? '2px solid #3b82f6' : '1px solid #475569',
                                background: billingCycle === 'weekly' ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                                color: billingCycle === 'weekly' ? '#60a5fa' : '#94a3b8',
                                cursor: 'pointer',
                                fontWeight: billingCycle === 'weekly' ? '600' : '400',
                                transition: 'all 0.2s'
                            }}
                        >
                            Weekly
                        </button>
                        <button 
                            className={`pricing__cycle-btn ${billingCycle === 'monthly' ? 'pricing__cycle-btn--active' : ''}`}
                            onClick={() => setBillingCycle('monthly')}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '8px',
                                border: billingCycle === 'monthly' ? '2px solid #3b82f6' : '1px solid #475569',
                                background: billingCycle === 'monthly' ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                                color: billingCycle === 'monthly' ? '#60a5fa' : '#94a3b8',
                                cursor: 'pointer',
                                fontWeight: billingCycle === 'monthly' ? '600' : '400',
                                transition: 'all 0.2s',
                                position: 'relative'
                            }}
                        >
                            Monthly
                            {monthlySavingsVsWeekly > 0 && (
                                <span style={{
                                    position: 'absolute',
                                    top: '-8px',
                                    right: '-8px',
                                    background: '#10b981',
                                    color: '#fff',
                                    fontSize: '0.65rem',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontWeight: '600'
                                }}>
                                    -{monthlySavingsVsWeekly}%
                                </span>
                            )}
                        </button>
                        <button 
                            className={`pricing__cycle-btn ${billingCycle === 'yearly' ? 'pricing__cycle-btn--active' : ''}`}
                            onClick={() => setBillingCycle('yearly')}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '8px',
                                border: billingCycle === 'yearly' ? '2px solid #f59e0b' : '1px solid #475569',
                                background: billingCycle === 'yearly' ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                                color: billingCycle === 'yearly' ? '#fbbf24' : '#94a3b8',
                                cursor: 'pointer',
                                fontWeight: billingCycle === 'yearly' ? '600' : '400',
                                transition: 'all 0.2s',
                                position: 'relative'
                            }}
                        >
                            Yearly
                            <span style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                background: '#f59e0b',
                                color: '#000',
                                fontSize: '0.65rem',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: '700'
                            }}>
                                BEST
                            </span>
                        </button>
                    </div>
                </header>

                {/* Status Bar */}
                <div className="pricing__status-bar">
                    <div className="pricing__status-label">
                        <span className={`pricing__status-dot ${isPremium ? 'pricing__status-dot--active' : ''}`} />
                        <span>System Status</span>
                    </div>
                    <p className="pricing__status-text">{statusText}</p>
                </div>

                <div className="pricing__grid">

                    {/* FREEMIUM CARD */}
                    <div className="pricing__card pricing__card--basic">
                        <div className="pricing__card-head">
                            <h2 className="pricing__plan-name">Freemium</h2>
                            <div className="pricing__price-row">
                                <span className="pricing__amount">$0</span>
                                <span className="pricing__period">/ forever</span>
                            </div>
                            <p className="pricing__desc">Essential daily predictions.</p>
                        </div>
                        
                        <ul className="pricing__features">
                            <li><Icon icon="solar:check-circle-linear" />Daily Football AI (Limited)</li>
                            <li><Icon icon="solar:check-circle-linear" />Multigoals & Draw Analysis</li>
                            <li><Icon icon="solar:check-circle-linear" />Basic Confidence Scores</li>
                            
                            <li className="pricing__feature-disabled"><Icon icon="solar:close-circle-linear" />V10.0 AI Engine</li>
                            <li className="pricing__feature-disabled"><Icon icon="solar:close-circle-linear" />🏀 Basketball V4 </li>
                            <li className="pricing__feature-disabled"><Icon icon="solar:close-circle-linear" />🎯 HRHR V5.5 </li>
                        </ul>

                        <button 
                            className="pricing__btn pricing__btn--ghost"
                            onClick={() => navigate('/ai-assistant')}
                        >
                            {isTrialActive ? 'Continue Trial' : 'Continue Free'}
                        </button>
                    </div>

                    {/* PREMIUM CARD */}
                    <div className="pricing__card pricing__card--premium">
                        <div className="pricing__card-badge">
                            {billingCycle === 'yearly' ? 'BEST VALUE' : 'RECOMMENDED'}
                        </div>
                        <div className="pricing__card-head">
                            <h2 className="pricing__plan-name">
                                {billingCycle === 'yearly' ? 'Annual Pass' : billingCycle === 'monthly' ? 'Monthly Pass' : 'Weekly Pass'}
                            </h2>
                            <div className="pricing__price-row">
                                <span className="pricing__amount">${currentPrice}</span>
                                <span className="pricing__period">/ {periodLabel}</span>
                            </div>
                            <p className="pricing__desc">Full access to every engine.</p>
                            
                            {/* Savings indicator */}
                            {billingCycle === 'yearly' && (
                                <p style={{ 
                                    color: '#4ade80', 
                                    fontSize: '0.85rem', 
                                    marginTop: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    justifyContent: 'center'
                                }}>
                                    <Icon icon="solar:tag-price-bold" />
                                    Save {yearlySavingsVsMonthly}% vs monthly
                                </p>
                            )}
                            {billingCycle === 'monthly' && monthlySavingsVsWeekly > 0 && (
                                <p style={{ 
                                    color: '#4ade80', 
                                    fontSize: '0.85rem', 
                                    marginTop: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    justifyContent: 'center'
                                }}>
                                    <Icon icon="solar:tag-price-bold" />
                                    Save {monthlySavingsVsWeekly}% vs weekly
                                </p>
                            )}
                        </div>

                        {/* 🔥 APPLE REQUIRED: Subscription Info Box */}
                        <div style={{
                            background: 'rgba(71, 85, 105, 0.2)',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '1rem',
                            fontSize: '0.8rem',
                            color: '#94a3b8'
                        }}>
                            <p style={{ margin: '0 0 4px 0' }}>
                                <strong style={{ color: '#e2e8f0' }}>
                                    AiPredict {billingCycle === 'yearly' ? 'Annual' : billingCycle === 'monthly' ? 'Monthly' : 'Weekly'} Premium
                                </strong>
                            </p>
                            <p style={{ margin: '0 0 4px 0' }}>
                                Auto-renews {billingCycle} at ${currentPrice}/{periodLabel} until cancelled.
                            </p>
                            <p style={{ margin: 0 }}>
                                Cancel anytime in {isIOS ? 'Settings → Subscriptions' : 'your Whop dashboard'}.
                            </p>
                        </div>

                        {/* 🔄 UPDATED: V10 limits changed to 4/day, 3 matches */}
                        <ul className="pricing__features">
                            <li><Icon icon="solar:check-circle-bold" />V10.0 AI Engine (4 searches/day)</li>
                            <li><Icon icon="solar:check-circle-bold" />Up to 3 matches per search</li>
                            
                            {/* 🏀 Basketball Feature */}
                            <li style={{ 
                                background: 'rgba(249, 115, 22, 0.1)', 
                                padding: '8px 12px', 
                                borderRadius: '8px',
                                margin: '4px 0'
                            }}>
                                <Icon icon="solar:check-circle-bold" style={{ color: '#f97316' }} />
                                <span style={{ color: '#f97316', fontWeight: '600' }}>
                                    🏀 Basketball V4 (3/day)
                                </span>
                            </li>
                            
                            {/* 🎯 HRHR Feature */}
                            <li style={{ 
                                background: 'rgba(16, 185, 129, 0.1)', 
                                padding: '8px 12px', 
                                borderRadius: '8px',
                                margin: '4px 0'
                            }}>
                                <Icon icon="solar:check-circle-bold" style={{ color: '#10b981' }} />
                                <span style={{ color: '#10b981', fontWeight: '600' }}>
                                    🎯 HRHR V5.5 (3/day)
                                </span>
                            </li>
                            
                            <li><Icon icon="solar:check-circle-bold" />V10.0 Engine Boost 90% Accuracy</li>
                            <li><Icon icon="solar:check-circle-bold" />Cancel Anytime</li>
                        </ul>

                        {/* PAYMENT ACTIONS - 🔥 SIMPLIFIED: No trial option */}
                        <div className="pricing__actions">
                            {isPaidPremium ? (
                                <button 
                                    className="pricing__btn pricing__btn--active"
                                    disabled
                                >
                                    <Icon icon="solar:verified-check-bold" style={{ fontSize: '1.2rem' }} />
                                    Plan Active
                                </button>
                            ) : (
                                <>
                                    {/* Existing trial user upgrade message */}
                                    {isTrialActive && (
                                        <p style={{ 
                                            marginBottom: '1rem', 
                                            fontSize: '0.85rem', 
                                            color: '#fbbf24', 
                                            textAlign: 'center',
                                            background: 'rgba(251, 191, 36, 0.1)',
                                            padding: '8px 12px',
                                            borderRadius: '6px'
                                        }}>
                                            ⏰ {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left on trial - Upgrade now to keep access!
                                        </p>
                                    )}

                                    {/* Trial Already Used Banner (for expired trial users) */}
                                    {hasUsedTrial && !isTrialActive && !isPaidPremium && (
                                        <div style={{
                                            background: 'rgba(239, 68, 68, 0.1)',
                                            border: '1px solid rgba(239, 68, 68, 0.3)',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            marginBottom: '1rem',
                                            textAlign: 'center'
                                        }}>
                                            <p style={{ color: '#f87171', fontSize: '0.9rem', margin: 0 }}>
                                                ⚠️ Trial expired
                                            </p>
                                            <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '4px', marginBottom: 0 }}>
                                                Subscribe to continue accessing premium features.
                                            </p>
                                        </div>
                                    )}

                                    {/* 🔥 SINGLE SUBSCRIBE BUTTON - Direct payment for everyone */}
                                    <button 
                                        className="pricing__btn pricing__btn--primary"
                                        onClick={handleSubscribe}
                                        disabled={purchasing}
                                        style={{
                                            width: '100%',
                                            padding: '14px 20px',
                                            borderRadius: '10px',
                                            border: 'none',
                                            background: 'linear-gradient(135deg, #10b981, #059669)',
                                            color: '#fff',
                                            cursor: purchasing ? 'not-allowed' : 'pointer',
                                            fontWeight: '600',
                                            fontSize: '1rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '10px',
                                            transition: 'all 0.2s',
                                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                            opacity: purchasing ? 0.7 : 1
                                        }}
                                    >
                                        <Icon icon={isIOS ? "simple-icons:apple" : "solar:card-linear"} style={{ fontSize: '1.2rem' }} />
                                        {purchasing ? 'Processing...' : premiumButtonLabel}
                                    </button>
                                    <p style={{ 
                                        color: '#94a3b8', 
                                        fontSize: '0.75rem', 
                                        textAlign: 'center',
                                        margin: '8px 0 0 0'
                                    }}>
                                        Full access immediately • 4 searches/day • Cancel anytime
                                    </p>
                                </>
                            )}
                        </div>

                        {/* 🔥 APPLE REQUIRED: Terms & Privacy Links */}
                        <div style={{ 
                            marginTop: '1rem', 
                            textAlign: 'center',
                            paddingTop: '1rem',
                            borderTop: '1px solid rgba(71, 85, 105, 0.3)'
                        }}>
                            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 8px 0' }}>
                                By subscribing, you agree to our:
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                <a 
                                    href={TERMS_OF_USE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ 
                                        color: '#60a5fa', 
                                        fontSize: '0.8rem', 
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    <Icon icon="solar:document-text-linear" />
                                    Terms of Use
                                </a>
                                <a 
                                    href={PRIVACY_POLICY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ 
                                        color: '#60a5fa', 
                                        fontSize: '0.8rem', 
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    <Icon icon="solar:shield-check-linear" />
                                    Privacy Policy
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 🏀🎯 FEATURES HIGHLIGHT SECTION */}
                <div style={{
                    marginTop: '2rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '1rem'
                }}>
                    {/* Basketball Card */}
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(234, 88, 12, 0.05) 100%)',
                        border: '1px solid rgba(249, 115, 22, 0.3)',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🏀</div>
                        <h3 style={{ color: '#f97316', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                            Basketball V4 
                        </h3>
                        <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1rem' }}>
                            AI predictions for NBA & European leagues with H2H analysis and fatigue detection.
                        </p>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            gap: '1rem',
                            flexWrap: 'wrap'
                        }}>
                            <span style={{ 
                                background: 'rgba(249, 115, 22, 0.2)', 
                                color: '#f97316',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: '600'
                            }}>
                                3 searches/day
                            </span>
                            <span style={{ 
                                background: 'rgba(249, 115, 22, 0.2)', 
                                color: '#f97316',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: '600'
                            }}>
                                2 games/search
                            </span>
                        </div>
                    </div>

                    {/* HRHR Card */}
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎯</div>
                        <h3 style={{ color: '#10b981', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                            HRHR V5.5
                        </h3>
                        <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1rem' }}>
                            High Risk High Reward value picks. Identifies mispriced markets for maximum edge.
                        </p>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            gap: '1rem',
                            flexWrap: 'wrap'
                        }}>
                            <span style={{ 
                                background: 'rgba(16, 185, 129, 0.2)', 
                                color: '#10b981',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: '600'
                            }}>
                                3 searches/day
                            </span>
                            <span style={{ 
                                background: 'rgba(16, 185, 129, 0.2)', 
                                color: '#10b981',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: '600'
                            }}>
                                3 matches/search
                            </span>
                        </div>
                    </div>
                </div>

                {/* PRICING COMPARISON TABLE */}
                <div style={{
                    marginTop: '2rem',
                    background: 'rgba(30, 41, 59, 0.5)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid rgba(71, 85, 105, 0.3)'
                }}>
                    <h3 style={{ color: '#fff', marginBottom: '1rem', textAlign: 'center' }}>
                        💰 Pricing Comparison
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid rgba(71, 85, 105, 0.5)' }}>
                                    <th style={{ textAlign: 'left', padding: '10px', color: '#94a3b8' }}>Plan</th>
                                    <th style={{ textAlign: 'center', padding: '10px', color: '#94a3b8' }}>USD</th>
                                    <th style={{ textAlign: 'center', padding: '10px', color: '#94a3b8' }}>Per Day</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style={{ borderBottom: '1px solid rgba(71, 85, 105, 0.3)' }}>
                                    <td style={{ padding: '10px', color: '#e2e8f0' }}>Weekly</td>
                                    <td style={{ padding: '10px', textAlign: 'center', color: '#e2e8f0' }}>${prices.weekly}</td>
                                    <td style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>${(prices.weekly / 7).toFixed(2)}</td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid rgba(71, 85, 105, 0.3)' }}>
                                    <td style={{ padding: '10px', color: '#e2e8f0' }}>Monthly</td>
                                    <td style={{ padding: '10px', textAlign: 'center', color: '#e2e8f0' }}>${prices.monthly}</td>
                                    <td style={{ padding: '10px', textAlign: 'center', color: '#4ade80' }}>${(prices.monthly / 30).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '10px', color: '#fbbf24', fontWeight: '600' }}>Yearly ⭐</td>
                                    <td style={{ padding: '10px', textAlign: 'center', color: '#fbbf24', fontWeight: '600' }}>${prices.yearly}</td>
                                    <td style={{ padding: '10px', textAlign: 'center', color: '#4ade80', fontWeight: '600' }}>${(prices.yearly / 365).toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* RESTORE PURCHASES (iOS Only) */}
                {isIOS && user && (
                    <div className="pricing__restore-section" style={{
                        marginTop: '2rem',
                        padding: '1.5rem',
                        background: 'rgba(30, 41, 59, 0.5)',
                        borderRadius: '12px',
                        textAlign: 'center',
                        border: '1px solid rgba(71, 85, 105, 0.3)'
                    }}>
                        <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '1rem' }}>
                            Previously purchased on another device?
                        </p>
                        <button 
                            className="pricing__btn pricing__btn--ghost"
                            onClick={handleRestorePurchases}
                            disabled={restoring}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px' }}
                        >
                            <Icon icon={restoring ? "solar:refresh-bold" : "solar:history-linear"} style={{ fontSize: '1.1rem', animation: restoring ? 'spin 1s linear infinite' : 'none' }} />
                            {restoring ? 'Restoring...' : 'Restore Purchases'}
                        </button>
                        <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.75rem' }}>
                            Uses the same Apple ID from your original purchase
                        </p>
                    </div>
                )}

                {/* FAQ SECTION - 🔥 UPDATED: Removed trial FAQ */}
                <div className="pricing__faq">
                    <h3 className="pricing__faq-title">Field Notes</h3>
                    
                    <div className="pricing__faq-grid">
                        <div className="pricing__faq-item">
                            <h4>Premium Benefits</h4>
                            <p>Premium unlocks 4 AI searches/day with up to 3 matches each, plus full access to 🏀 Basketball V4, 🎯 HRHR V5.5 and Crypto AI.</p>
                        </div>
                        <div className="pricing__faq-item">
                            <h4>🏀 Basketball </h4>
                            <p>Our basketball engine analyzes NBA & European leagues with H2H records, fatigue detection, and travel schedules. 3 searches/day, max 2 games per search.</p>
                        </div>
                        <div className="pricing__faq-item">
                            <h4>🎯 HRHR </h4>
                            <p>High Risk High Reward identifies mispriced betting markets where true probability exceeds bookmaker odds. 3 searches/day, max 3 matches per search.</p>
                        </div>
                        <div className="pricing__faq-item">
                            <h4>Payment Methods</h4>
                            <p>
                                {isIOS 
                                    ? "We accept Apple Pay directly within the app for instant access."
                                    : "We accept International Cards via Whop for secure checkout."
                                }
                            </p>
                        </div>
                    </div>
                </div>

                {/* 🔥 FOOTER LEGAL LINKS (Apple Requirement) */}
                <div style={{
                    marginTop: '2rem',
                    paddingTop: '1.5rem',
                    borderTop: '1px solid rgba(71, 85, 105, 0.3)',
                    textAlign: 'center'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap', marginBottom: '1rem' }}>
                        <a 
                            href={TERMS_OF_USE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#64748b', fontSize: '0.8rem', textDecoration: 'none' }}
                        >
                            Terms of Use (EULA)
                        </a>
                        <a 
                            href={PRIVACY_POLICY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#64748b', fontSize: '0.8rem', textDecoration: 'none' }}
                        >
                            Privacy Policy
                        </a>
                        <button
                            onClick={() => navigate('/legal?tab=risk')}
                            style={{ 
                                color: '#64748b', 
                                fontSize: '0.8rem', 
                                textDecoration: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            Risk Disclosure
                        </button>
                    </div>
                    <p style={{ color: '#475569', fontSize: '0.75rem' }}>
                        © {new Date().getFullYear()} AiPredict. All rights reserved.
                    </p>
                </div>

            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
            
        </section>
    );
};

export default Pricing;